import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiShoppingCart, FiClock, FiUsers, FiCheck } from 'react-icons/fi';
import Header from './Header';
import CostCalculator from './CostCalculator';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 120px 20px 60px;
  min-height: 100vh;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const Title = styled(motion.h1)`
  font-size: 3rem;
  font-weight: bold;
  color: var(--primary-blue);
  margin-bottom: 20px;
`;

const Subtitle = styled(motion.p)`
  font-size: 1.2rem;
  color: var(--medium-gray);
  max-width: 600px;
  margin: 0 auto;
`;

const PlanSelector = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 40px;
`;

const PlanButton = styled.button`
  padding: 15px 30px;
  border: 2px solid var(--primary-blue);
  background: ${props => props.active ? 'var(--primary-blue)' : 'white'};
  color: ${props => props.active ? 'white' : 'var(--primary-blue)'};
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: var(--primary-blue);
    color: white;
  }
`;

const PlanContainer = styled(motion.div)`
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const PlanHeader = styled.div`
  background: linear-gradient(135deg, var(--primary-blue), var(--accent-orange));
  color: white;
  padding: 30px;
  text-align: center;
`;

const PlanTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 10px;
`;

const PlanSubtitle = styled.p`
  font-size: 1.2rem;
  opacity: 0.9;
`;

const PlanContent = styled.div`
  padding: 40px;
`;

const Section = styled.div`
  margin-bottom: 40px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid var(--light-gray);
`;

const SectionIcon = styled.div`
  background: var(--primary-blue);
  color: white;
  padding: 10px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SectionTitle = styled.h3`
  font-size: 1.5rem;
  color: var(--primary-blue);
  margin: 0;
`;

const StepTitle = styled.h4`
  font-size: 1.2rem;
  color: var(--dark-gray);
  margin: 20px 0 10px 0;
  font-weight: 600;
`;

const ShoppingList = styled.div`
  background: #f8fafc;
  border-radius: 10px;
  padding: 20px;
  margin: 15px 0;
`;

const ShoppingCategory = styled.div`
  margin-bottom: 15px;
`;

const CategoryTitle = styled.h5`
  color: var(--primary-blue);
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ItemList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ListItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 0;
  color: var(--dark-gray);
`;

const CheckIcon = styled(FiCheck)`
  color: var(--success-green);
  font-size: 1.1rem;
`;

const InstructionText = styled.p`
  color: var(--dark-gray);
  line-height: 1.6;
  margin: 10px 0;
`;

const HighlightBox = styled.div`
  background: linear-gradient(135deg, #fff3e0, #ffe0b2);
  border-left: 4px solid var(--accent-orange);
  padding: 20px;
  border-radius: 8px;
  margin: 15px 0;
`;

const TimeEstimate = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: #e3f2fd;
  padding: 15px;
  border-radius: 8px;
  margin: 20px 0;
  color: var(--primary-blue);
  font-weight: 600;
`;

const AssemblyStep = styled.div`
  background: #f1f8e9;
  border: 1px solid #c8e6c9;
  border-radius: 8px;
  padding: 15px;
  margin: 10px 0;
`;

const AssemblyOrder = styled.ol`
  margin: 0;
  padding-left: 20px;
  
  li {
    margin: 8px 0;
    color: var(--dark-gray);
    line-height: 1.5;
  }
`;

const plans = {
  210: {
    title: "210 Burritos Event Plan",
    shopping: {
      proteins: [
        "3 packs of Black Beans (8 cans per pack) → 24 cans total",
        "1 pack of Corn (contains 12 cans)"
      ],
      grains: [
        "24 cups of dry organic brown rice (will yield ~50 cups cooked)"
      ],
      veggies: [
        "4 Costco bags of lettuce",
        "4 bags of Bell Peppers (36 total)"
      ],
      condiments: [
        "2 packs of Costco Guacamole (each pack contains 3) → 6 containers total",
        "2 Sour Cream from Costco",
        "3 bags of 2-pack Mexican Cheese from Costco (6 packs total)",
        "2 large jars of salsa (medium) from Costco",
        "1 large bottle of pickled jalapenos (Costco/save mart)"
      ],
      tortillas: [
        "7 packs of Jumbo Tortillas (each 30 count) → 210 tortillas total"
      ]
    },
    cooking: {
      beans: "Open 24 cans of black beans, 6 cans of corn, and 3 bottles of salsa, then drain any excess liquid. Prepare the mixture in three separate batches using a 6-cup Instant Pot per batch: Add 8 cans of black beans, 2 cans of corn, and 1 bottle of salsa per batch. Season with Mexican taco seasoning mix or Indian chaat masala and dried kasoori methi for added flavor. Let simmer for 20-30 minutes on medium, allowing the flavors to blend together. Mix a cup of drained jalapenos to the bean mixture after it cools down.",
      rice: "Cook 24 cups of dry rice, yielding approximately 40-50 cups of cooked rice. Divide the rice into two batches, using an 8-cup Instant Pot for each batch. Add salt, olive oil and a splash of lime juice (1 lemon) for enhanced flavor. Set the delay timer to start cooking at 5 AM, ensuring the rice is ready in about 1 hour."
    },
    distribution: [
      "Agape: Last Saturday of the month - 180 burritos",
      "Drop off burritos at San Jose",
      "Loretto: Every other Saturday - 210 burritos",
      "Pickup for distribution",
      "Help pack the burritos along with water bottle in individual packs and load in her car"
    ]
  },
  400: {
    title: "400 Burritos Event Plan",
    shopping: {
      proteins: [
        "7 packs of Black Beans (8 cans per pack) → 56 cans total OR",
        "7 Cans of 108 OZ Large Beans from Smart and Final",
        "1 pack of Corn (contains 12 cans)"
      ],
      grains: [
        "60 cups of dry organic brown rice (will yield ~125 cups cooked)"
      ],
      veggies: [
        "10 Costco bags of lettuce",
        "8 Bags of Bell Peppers (72 total)"
      ],
      condiments: [
        "6 packs of Costco Guacamole (each pack contains 3) → 18 containers total",
        "5 Sour Cream from Costco",
        "8 bags of 2-pack Mexican Cheese from Costco (16 packs total)",
        "5 large jars of salsa (medium) from Costco",
        "2 large bottle of pickled jalapenos (Costco/save mart)"
      ],
      tortillas: [
        "14 packs of Jumbo Tortillas (each 30 count) → 420 tortillas total"
      ],
      seasoning: [
        "Mexican taco seasoning mix or Indian chaat masala",
        "Dried kasoori methi for added flavor"
      ]
    },
    cooking: {
      beans: "Scale up the bean preparation proportionally. Use multiple Instant Pots for larger batches. Season with Mexican taco seasoning mix or Indian chaat masala and dried kasoori methi for added flavor. Let simmer for 20-30 minutes on medium, allowing the flavors to blend together. Mix drained jalapenos to the bean mixture after it cools down.",
      rice: "Cook 60 cups of dry rice, yielding approximately 120-125 cups of cooked rice. Use multiple 8-cup Instant Pots for batch cooking. Add salt, olive oil and a splash of lime juice for enhanced flavor. Set delay timers to ensure rice is ready when needed."
    },
    distribution: [
      "Large-scale distribution events",
      "Multiple community partner locations", 
      "Coordinate with additional volunteers for transport",
      "Ensure proper storage and temperature control for larger quantities"
    ]
  }
};

const EventPlanning = () => {
  const [selectedPlan, setSelectedPlan] = useState('210');
  const currentPlan = plans[selectedPlan];

  const assemblySteps = [
    "Silver Foil → Place the foil on a large serving tray with the shiny side facing down",
    "Tortillas → Warm each tortilla for a few seconds on both sides using an electric griddle",
    "Cheese → Evenly sprinkle a handful on one half of the tortilla",
    "Rice → Spread a thin layer of rice over the cheese",
    "Beans & Corn → Add a scoop of the black bean and corn mixture on top of the rice",
    "Guacamole → Spoon a small amount over the beans",
    "Sour Cream → Add a small dollop",
    "Bell Peppers & Lettuce → Sprinkle a handful of mixed veggies over the sour cream",
    "Wrap Burritos: Fold the burritos tightly by sealing the sides first, tucking in the ingredients securely, and rolling them firmly",
    "Wrap each burrito in foil and place them in a carton"
  ];

  return (
    <>
      <Header />
      <Container>
        <PageHeader>
          <Title
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            🌯 Burrito-Making Event Planning
          </Title>
          <Subtitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Complete step-by-step guides to ensure smooth and efficient burrito-making events
          </Subtitle>
        </PageHeader>

        <PlanSelector>
          <PlanButton 
            active={selectedPlan === '210'} 
            onClick={() => setSelectedPlan('210')}
          >
            210 Burritos Plan
          </PlanButton>
          <PlanButton 
            active={selectedPlan === '400'} 
            onClick={() => setSelectedPlan('400')}
          >
            400 Burritos Plan
          </PlanButton>
        </PlanSelector>

        <CostCalculator />

        <PlanContainer
          key={selectedPlan}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <PlanHeader>
            <PlanTitle>{currentPlan.title}</PlanTitle>
            <PlanSubtitle>A step-by-step guide to ensure a smooth and efficient event!</PlanSubtitle>
          </PlanHeader>

          <PlanContent>
            {/* Day Before Section */}
            <Section>
              <SectionHeader>
                <SectionIcon><FiShoppingCart /></SectionIcon>
                <SectionTitle>Day Before the Event (Friday Evening)</SectionTitle>
              </SectionHeader>

              <StepTitle>Step 1: Grocery Shopping</StepTitle>
              <InstructionText>
                Ensure you buy all necessary ingredients in bulk to accommodate {selectedPlan} burritos. 
                Here's your shopping list:
              </InstructionText>

              <ShoppingList>
                <ShoppingCategory>
                  <CategoryTitle>
                    <CheckIcon /> Proteins & Fillings:
                  </CategoryTitle>
                  <ItemList>
                    {currentPlan.shopping.proteins.map((item, index) => (
                      <ListItem key={index}>
                        <CheckIcon />
                        {item}
                      </ListItem>
                    ))}
                  </ItemList>
                </ShoppingCategory>

                <ShoppingCategory>
                  <CategoryTitle>
                    <CheckIcon /> Grains:
                  </CategoryTitle>
                  <ItemList>
                    {currentPlan.shopping.grains.map((item, index) => (
                      <ListItem key={index}>
                        <CheckIcon />
                        {item}
                      </ListItem>
                    ))}
                  </ItemList>
                </ShoppingCategory>

                <ShoppingCategory>
                  <CategoryTitle>
                    <CheckIcon /> Veggies:
                  </CategoryTitle>
                  <ItemList>
                    {currentPlan.shopping.veggies.map((item, index) => (
                      <ListItem key={index}>
                        <CheckIcon />
                        {item}
                      </ListItem>
                    ))}
                  </ItemList>
                </ShoppingCategory>

                <ShoppingCategory>
                  <CategoryTitle>
                    <CheckIcon /> Condiments & Extras:
                  </CategoryTitle>
                  <ItemList>
                    {currentPlan.shopping.condiments.map((item, index) => (
                      <ListItem key={index}>
                        <CheckIcon />
                        {item}
                      </ListItem>
                    ))}
                  </ItemList>
                </ShoppingCategory>

                <ShoppingCategory>
                  <CategoryTitle>
                    <CheckIcon /> Tortillas:
                  </CategoryTitle>
                  <ItemList>
                    {currentPlan.shopping.tortillas.map((item, index) => (
                      <ListItem key={index}>
                        <CheckIcon />
                        {item}
                      </ListItem>
                    ))}
                  </ItemList>
                </ShoppingCategory>

                {currentPlan.shopping.seasoning && (
                  <ShoppingCategory>
                    <CategoryTitle>
                      <CheckIcon /> Seasoning:
                    </CategoryTitle>
                    <ItemList>
                      {currentPlan.shopping.seasoning.map((item, index) => (
                        <ListItem key={index}>
                          <CheckIcon />
                          {item}
                        </ListItem>
                      ))}
                    </ItemList>
                  </ShoppingCategory>
                )}
              </ShoppingList>

              <StepTitle>Step 2: Prep & Cooking</StepTitle>
              
              <HighlightBox>
                <h5 style={{ color: 'var(--accent-orange)', marginBottom: '10px' }}>
                  1. Cook the Beans, Corn, and Salsa
                </h5>
                <InstructionText>{currentPlan.cooking.beans}</InstructionText>
              </HighlightBox>

              <HighlightBox>
                <h5 style={{ color: 'var(--accent-orange)', marginBottom: '10px' }}>
                  2. Cook the Rice
                </h5>
                <InstructionText>{currentPlan.cooking.rice}</InstructionText>
              </HighlightBox>
            </Section>

            {/* Day of Event Section */}
            <Section>
              <SectionHeader>
                <SectionIcon><FiUsers /></SectionIcon>
                <SectionTitle>Day of the Event (Saturday AM)</SectionTitle>
              </SectionHeader>

              <StepTitle>@Home</StepTitle>
              <InstructionText>
                Wash Veggies, load all the burrito event stuff in van
              </InstructionText>

              <StepTitle>@SCC Center - Meet & Greet Volunteers</StepTitle>
              <InstructionText>
                With the help of the volunteers:
              </InstructionText>
              <ItemList>
                <ListItem><CheckIcon />Unload all burrito-making supplies from van</ListItem>
                <ListItem><CheckIcon />Set up two burrito stations</ListItem>
                <ListItem><CheckIcon />Cover the tables with tablecloths</ListItem>
              </ItemList>

              <HighlightBox>
                <h5 style={{ color: 'var(--accent-orange)', marginBottom: '10px' }}>
                  Sanitation & Efficiency Reminders:
                </h5>
                <ItemList>
                  <ListItem><CheckIcon />Wash hands before starting, wear gloves and hair nets</ListItem>
                  <ListItem><CheckIcon />Keep the station clean & organized</ListItem>
                  <ListItem><CheckIcon />Work quickly but carefully to maintain a steady flow</ListItem>
                  <ListItem><CheckIcon />Introduction of all the volunteers to each other</ListItem>
                </ItemList>
              </HighlightBox>

              <StepTitle>Chop & Prep Veggies</StepTitle>
              <ItemList>
                <ListItem><CheckIcon />Bell Peppers → Deseed, and dice into small pieces</ListItem>
                <ListItem><CheckIcon />Lettuce → Chop finely for easy wrapping</ListItem>
                <ListItem><CheckIcon />Combine the chopped bell peppers and lettuce in two large chafing dishes, mixing them evenly</ListItem>
              </ItemList>

              <StepTitle>Assign Volunteers & Give Instructions</StepTitle>
              <InstructionText>
                Assign at least 12-14 volunteers to different tasks, with one person assigned to each task on each side of the table:
              </InstructionText>
              <ItemList>
                <ListItem><CheckIcon />1 person warming tortillas on electric griddle for 10 secs on each side of the tortilla</ListItem>
                <ListItem><CheckIcon />1 person placing silver foil in the serving tray</ListItem>
                <ListItem><CheckIcon />2 people handling cheese</ListItem>
                <ListItem><CheckIcon />2 people managing rice</ListItem>
                <ListItem><CheckIcon />2 people handling beans & corn</ListItem>
                <ListItem><CheckIcon />2 people managing sour cream</ListItem>
                <ListItem><CheckIcon />2 people managing guacamole</ListItem>
                <ListItem><CheckIcon />2 people adding veggies (bell peppers & lettuce)</ListItem>
                <ListItem><CheckIcon />2 people wrapping burritos</ListItem>
                <ListItem><CheckIcon />2 people organizing/storing completed burritos</ListItem>
                <ListItem><CheckIcon />1 person cleaning the service tray and returning it to the start of the assembly line for reuse</ListItem>
              </ItemList>
            </Section>

            {/* Assembly Section */}
            <Section>
              <SectionHeader>
                <SectionIcon><FiCheck /></SectionIcon>
                <SectionTitle>Step 3: Burrito Assembly Line</SectionTitle>
              </SectionHeader>

              <AssemblyStep>
                <h5 style={{ color: 'var(--primary-blue)', marginBottom: '15px' }}>Assembly Order:</h5>
                <AssemblyOrder>
                  {assemblySteps.map((step, index) => (
                    <li key={index}>{step}</li>
                  ))}
                </AssemblyOrder>
              </AssemblyStep>

              <HighlightBox>
                <h5 style={{ color: 'var(--accent-orange)', marginBottom: '10px' }}>
                  Additional Notes:
                </h5>
                <ItemList>
                  <ListItem><CheckIcon />Volunteers rotate if needed to avoid fatigue</ListItem>
                  <ListItem><CheckIcon />Take a few pictures of volunteers making the burritos</ListItem>
                  <ListItem><CheckIcon />Keep track of ingredient portions to ensure all {selectedPlan} burritos are evenly filled</ListItem>
                </ItemList>
              </HighlightBox>

              <StepTitle>Distribution:</StepTitle>
              <ItemList>
                {currentPlan.distribution.map((item, index) => (
                  <ListItem key={index}>
                    <CheckIcon />
                    {item}
                  </ListItem>
                ))}
              </ItemList>

              <HighlightBox>
                <h5 style={{ color: 'var(--accent-orange)', marginBottom: '10px' }}>
                  Group Picture:
                </h5>
                <InstructionText>
                  At the end of the event, gather all the volunteers for a group photo with the completed burritos 
                  ready for distribution to the unhoused and underserved community. Take a moment to thank all the 
                  volunteers for their hard work and for making a difference in the lives of those in need.
                </InstructionText>
              </HighlightBox>
            </Section>

            {/* Cleanup Section */}
            <Section>
              <SectionHeader>
                <SectionIcon><FiClock /></SectionIcon>
                <SectionTitle>Step 4: Post-Event Cleanup (30 mins)</SectionTitle>
              </SectionHeader>

              <StepTitle>1. Clean the Burrito Stations</StepTitle>
              <ItemList>
                <ListItem><CheckIcon />Wipe down all tables with disinfectant</ListItem>
                <ListItem><CheckIcon />Dispose of used foil, parchment paper, and gloves</ListItem>
              </ItemList>

              <StepTitle>2. Pack & Store Leftovers (if any)</StepTitle>
              <ItemList>
                <ListItem><CheckIcon />Unused ingredients (cheese, sour cream, guacamole, salsa) should be refrigerated</ListItem>
                <ListItem><CheckIcon />Leftover burritos can be stored in trays or coolers for later distribution</ListItem>
              </ItemList>

              <StepTitle>3. Take Out Trash & Final Cleanup</StepTitle>
              <ItemList>
                <ListItem><CheckIcon />Empty trash bins and dispose of waste properly</ListItem>
                <ListItem><CheckIcon />Vacuum the area if needed</ListItem>
              </ItemList>

              <StepTitle>@Home - 4. Wash & Store Utensils (1hr)</StepTitle>
              <ItemList>
                <ListItem><CheckIcon />Wash all instant pots, other utensils, spoons, tongs, bowls, trays, and cutting boards used</ListItem>
                <ListItem><CheckIcon />Dry and store them properly</ListItem>
              </ItemList>
            </Section>

            {/* Time Estimates */}
            <Section>
              <SectionHeader>
                <SectionIcon><FiClock /></SectionIcon>
                <SectionTitle>Estimated Time for Each Step</SectionTitle>
              </SectionHeader>

              <TimeEstimate>
                <FiShoppingCart />
                Shopping: 1.5 - 2 hours
              </TimeEstimate>
              <TimeEstimate>
                <FiClock />
                Cooking Beans, Rice & Corn: 1.5 - 2 hours
              </TimeEstimate>
              <TimeEstimate>
                <FiUsers />
                Assembly Line Setup: 10 minutes
              </TimeEstimate>
              <TimeEstimate>
                <FiCheck />
                Chopping & Prepping Veggies: 15 minutes
              </TimeEstimate>
              <TimeEstimate>
                <FiUsers />
                Assembly Line: 1 - 1.5 hours (with approximately 20 volunteers)
              </TimeEstimate>
              <TimeEstimate>
                <FiCheck />
                Post-Event Cleanup at SCC: 30 minutes
              </TimeEstimate>
              <TimeEstimate>
                <FiClock />
                Cleanup Utensils at Home: 1 hour
              </TimeEstimate>
            </Section>
          </PlanContent>
        </PlanContainer>
      </Container>
    </>
  );
};

export default EventPlanning; 